import { forwardRef } from 'react';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { Button, type ButtonProps } from '@mui/material';

export type FilterToolbarButtonProps = Omit<ButtonProps, 'size' | 'sx' | 'variant'> & {
  /** On-state for the controls in this row that toggle rather than open a
   *  menu. Same treatment as a selected rail item, so "on" reads the same
   *  everywhere in the list chrome. */
  selected?: boolean;
};

/** One trigger treatment for the compact controls that sit beside list filters. */
export const FilterToolbarButton = forwardRef<HTMLButtonElement, FilterToolbarButtonProps>(
  ({ children, endIcon, selected, ...props }, ref) => {
    const expanded = props['aria-expanded'] === true;

    return (
      <Button
        {...props}
        ref={ref}
        /* A menu trigger gets the chevron; a toggle passes its own end icon
           (or `null` for none), because a chevron on a control that opens
           nothing is a promise the button cannot keep. */
        endIcon={
          endIcon === undefined ? (
            <KeyboardArrowDownRoundedIcon
              sx={{
                fontSize: '1rem !important',
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 140ms ease',
              }}
            />
          ) : (
            endIcon
          )
        }
        size="small"
        sx={{
          bgcolor: selected ? 'action.selected' : 'background.paper',
          borderColor: selected ? 'text.disabled' : 'divider',
          color: 'text.primary',
          fontWeight: 600,
          height: 32,
          px: 1.25,
          textTransform: 'none',
          whiteSpace: 'nowrap',
          '&:hover': {
            bgcolor: selected ? 'action.selected' : 'action.hover',
            borderColor: 'text.disabled',
          },
          '& .MuiButton-endIcon': { ml: 0.5 },
          '& .MuiButton-startIcon': { mr: 0.75 },
          '@media (prefers-reduced-motion: reduce)': {
            '& .MuiButton-endIcon svg': { transition: 'none' },
          },
        }}
        variant="outlined"
      >
        {children}
      </Button>
    );
  },
);

FilterToolbarButton.displayName = 'FilterToolbarButton';
